import { useState } from "react";

import { Link, useNavigate, useLocation } from "@remix-run/react";
import clsx from "clsx";
import { jsPDF } from "jspdf";
import { ChevronLeft, LogOut } from "lucide-react";

import Logo, { DisplayHeader, LogoSmall } from "~/components/logo";
import { Button } from "~/components/ui/button";
import { H2, H3, H4, P, A, UL, LI } from "~/components/ui/typography";
import { useToast } from "~/components/ui/use-toast";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import {
    baaDocument,
    getSectionIds,
    getSectionById,
} from "~/data/baa-document";
import { useAnonymous } from "~/hooks/use-anonymous";
import { useIsMobile } from "~/hooks/use-mobile";
import { handleLogout } from "~/lib/logout";
import { cn } from "~/lib/utils";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { SUPPORT_EMAIL } from "~/lib/env.shared";

// Create modified sections with a combined "preamble" section first
const modifiedSections = [
    { id: "preamble", title: "Preamble" },
    ...baaDocument.sections,
];

export default function BAA() {
    const { toast } = useToast();
    const [currentSection, setCurrentSection] = useState<string>("preamble");

    // Header-related hooks
    const navigate = useNavigate();
    const location = useLocation();
    const isRoot =
        location.pathname === "/" || location.pathname === "/landing";
    const { user } = useSupabaseSession();
    const isMobile = useIsMobile();
    const { isAnonymous } = useAnonymous();

    const handleSignOut = () => {
        handleLogout();
    };

    // Function to generate PDF with the entire legal document content
    const generateFormalPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margins = 20;
        const textWidth = pageWidth - margins * 2;
        let currentY = 20;
        let pageNum = 1;

        // Add header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(baaDocument.title, pageWidth / 2, currentY, {
            align: "center",
        });
        currentY += 20;

        // Helper function to add text with proper formatting and pagination
        const addText = (
            text: string,
            startY: number,
            fontSize = 11,
            bold = false,
        ) => {
            if (!text) return startY;

            doc.setFont("helvetica", bold ? "bold" : "normal");
            doc.setFontSize(fontSize);

            const textLines = doc.splitTextToSize(text, textWidth);

            for (let i = 0; i < textLines.length; i++) {
                // Check if we need a new page
                if (startY > pageHeight - margins) {
                    doc.addPage();
                    pageNum++;
                    startY = margins + 10;

                    // No page numbers added here - we'll add them all at the end
                }

                doc.text(textLines[i], margins, startY);
                startY += fontSize * 0.5;
            }

            return startY + fontSize * 0.5;
        };

        // Add section with title and content
        const addSection = (
            title: string,
            content: string,
            currentY: number,
            isBold = true,
        ) => {
            let y = currentY;

            // Add section title if provided
            if (title) {
                // Check if need new page for title
                if (y > pageHeight - margins) {
                    doc.addPage();
                    pageNum++;
                    y = margins + 10;

                    // No page numbers added here - we'll add them all at the end
                }

                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text(title, margins, y);
                y += 8;
            }

            // Add content if provided
            if (content) {
                y = addText(content, y, 11, isBold);
            }

            return y + 10;
        };

        // Add preamble
        currentY = addText(baaDocument.introduction.text, currentY, 11, true);
        currentY += 10;

        // Add recitals
        currentY = addSection(baaDocument.recitals.title, "", currentY);
        for (const item of baaDocument.recitals.items) {
            currentY = addText(item.text, currentY);
            currentY += 5;
        }
        currentY += 5;

        // Add agreement text
        currentY = addText(baaDocument.agreement.text, currentY);
        currentY += 10;

        // Add all sections
        for (const section of baaDocument.sections) {
            // Add section title
            currentY = addSection(section.title, section.text || "", currentY);

            // Add terms if available
            if (section.terms) {
                for (const term of section.terms) {
                    const termText = `${term.number}. ${term.term} ${term.definition}`;
                    currentY = addText(termText, currentY);
                    currentY += 5;
                }
            }

            // Add clauses if available
            if (section.clauses) {
                for (const clause of section.clauses) {
                    // Check if title exists and handle differently than regular text
                    if (
                        "title" in clause &&
                        clause.title &&
                        typeof clause.title === "string"
                    ) {
                        // For clauses with a title, format as "number. title"
                        const titleLine = `${clause.number}. ${clause.title}`;

                        // Add the formatted title as bold text
                        currentY = addText(titleLine, currentY, 11, true);
                        currentY += 3;

                        // Then add the clause text as normal
                        if (clause.text) {
                            currentY = addText(
                                clause.text,
                                currentY,
                                11,
                                false,
                            );
                            currentY += 5;
                        }
                    } else {
                        // For clauses without a separate title, prepend the number to the text
                        // and let the PDF engine handle line wrapping properly
                        const fullText = clause.text || "";
                        const numberedText = `${clause.number}. ${fullText}`;

                        // Add the full text with number prepended
                        currentY = addText(numberedText, currentY, 11, true);
                        currentY += 5;
                    }

                    // Add subclauses if available
                    if (
                        "subclauses" in clause &&
                        clause.subclauses &&
                        Array.isArray(clause.subclauses)
                    ) {
                        for (const subclause of clause.subclauses) {
                            currentY = addText(
                                `${subclause.letter}. ${subclause.text}`,
                                currentY,
                                11,
                                false,
                            );
                            currentY += 5;
                        }
                    }

                    currentY += 5;
                }
            }

            // Add contact info if available (for Notice section)
            if (section.contact_info) {
                if (section.contact_info.covered_entity) {
                    currentY = addText(
                        section.contact_info.covered_entity.title,
                        currentY,
                        11,
                        true,
                    );
                    currentY += 5;

                    for (const field of section.contact_info.covered_entity
                        .fields) {
                        const fieldText = field.value
                            ? `${field.label}: ${field.value}`
                            : field.label;
                        currentY = addText(fieldText, currentY);
                        currentY += 3;
                    }

                    currentY += 5;
                }

                if (section.contact_info.business_associate) {
                    currentY = addText(
                        section.contact_info.business_associate.title,
                        currentY,
                        11,
                        true,
                    );
                    currentY += 5;

                    for (const field of section.contact_info.business_associate
                        .fields) {
                        const fieldText = field.value
                            ? `${field.label}: ${field.value}`
                            : field.label;
                        currentY = addText(fieldText, currentY);
                        currentY += 3;
                    }

                    currentY += 5;
                }
            }

            currentY += 5;
        }

        // Add signature section
        currentY = addText(baaDocument.signatures.title, currentY, 11, true);
        currentY += 15;

        const signatureAreaWidth = (pageWidth - margins * 2) / 2;

        doc.text(
            baaDocument.signatures.parties[0]?.name || "Your Company Name",
            margins,
            currentY,
        );
        doc.text(
            baaDocument.signatures.parties[1]?.role || "(Covered Entity)",
            pageWidth - margins - signatureAreaWidth,
            currentY,
        );
        currentY += 20;

        doc.text("Signed", margins, currentY);
        doc.text("Signed", pageWidth - margins - signatureAreaWidth, currentY);
        currentY += 20;

        doc.text("Printed", margins, currentY);
        doc.text("Printed", pageWidth - margins - signatureAreaWidth, currentY);
        currentY += 20;

        doc.text("Date", margins, currentY);
        doc.text("Date", pageWidth - margins - signatureAreaWidth, currentY);

        // Add signature lines
        currentY -= 15; // Move back up to add lines under each field
        const lineWidth = signatureAreaWidth - 20;

        // Add line under Date (left side)
        doc.line(margins, currentY + 5, margins + lineWidth, currentY + 5);

        // Add line under Date (right side)
        doc.line(
            pageWidth - margins - lineWidth - 20,
            currentY + 5,
            pageWidth - margins - 15,
            currentY + 5,
        );

        // Move back up to add lines under Printed
        currentY -= 20;

        // Add line under Printed (left side)
        doc.line(margins, currentY + 5, margins + lineWidth, currentY + 5);

        // Add line under Printed (right side)
        doc.line(
            pageWidth - margins - lineWidth - 20,
            currentY + 5,
            pageWidth - margins - 15,
            currentY + 5,
        );

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");

        doc.text(
            "Adam Taylor (Evolve Labs Inc.)",
            pageWidth - margins - signatureAreaWidth,
            currentY + 3,
        );

        // Move back up to add lines under Signed
        currentY -= 20;

        // Add line under Signed (left side)
        doc.line(margins, currentY + 5, margins + lineWidth, currentY + 5);

        // Add line under Signed (right side)
        doc.line(
            pageWidth - margins - lineWidth - 20,
            currentY + 5,
            pageWidth - margins - 15,
            currentY + 5,
        );

        // Add page numbers to all pages - THIS IS THE ONLY PLACE WE ADD PAGE NUMBERS NOW
        for (let i = 1; i <= pageNum; i++) {
            doc.setPage(i);

            // Save current font settings
            const fontSettings = doc.getFont();
            const fontSize = doc.getFontSize();

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(
                `Page ${i} of ${pageNum}`,
                pageWidth - margins,
                pageHeight - 10,
                {
                    align: "right",
                },
            );

            // Restore previous font settings
            doc.setFontSize(fontSize);
            doc.setFont(fontSettings.fontName, fontSettings.fontStyle);
        }

        return doc;
    };

    const handleDownloadPDF = async () => {
        try {
            const doc = await generateFormalPDF();
            const pdfDataUri = doc.output("datauristring");
            triggerDownload(pdfDataUri, "Business_Associate_Agreement.pdf");

            toast({
                title: "PDF Generated Successfully",
                description:
                    "Your Business Associate Agreement has been downloaded.",
            });
        } catch (error) {
            /*console.error("Error generating PDF:", error);*/
            toast({
                variant: "destructive",
                title: "PDF Generation Failed",
                description:
                    "There was an error generating your PDF. Please try again.",
            });
        }
    };

    const triggerDownload = (url: string, filename: string) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Get the current section data from standard sections
    const standardSectionData = getSectionById(currentSection);

    // Function to render content based on selected section
    const renderSectionContent = () => {
        const currentSectionId = currentSection;
        let content;

        if (currentSectionId === "preamble") {
            content = (
                <>
                    <H2 className="mb-4 text-2xl font-bold">Introduction</H2>
                    <div className="space-y-4">
                        <P className="text-gray-700">
                            {baaDocument.introduction.text}
                        </P>
                        <div className="mt-3 rounded-md bg-gray-50 p-3">
                            <P className="text-sm text-gray-600">
                                This section introduces the agreement and sets
                                the context for the relationship between the
                                parties.
                            </P>
                        </div>
                    </div>

                    <div className="mt-8">
                        <H2 className="mb-4 text-2xl font-bold">
                            {baaDocument.recitals.title}
                        </H2>
                        {baaDocument.recitals.items.map((item, index) => (
                            <div key={index} className="space-y-4">
                                <P className="text-gray-700">{item.text}</P>
                                <div className="mt-3 rounded-md bg-gray-50 p-3">
                                    <P className="text-sm text-gray-600">
                                        This recital establishes key facts and
                                        context about the agreement.
                                    </P>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8">
                        <H2 className="mb-4 text-2xl font-bold">Agreement</H2>
                        <div className="space-y-4">
                            <P className="font-semibold text-gray-700">
                                {baaDocument.agreement.text}
                            </P>
                            <div className="mt-3 rounded-md bg-gray-50 p-3">
                                <P className="text-sm text-gray-600">
                                    This section formalizes the agreement
                                    between the parties.
                                </P>
                            </div>
                        </div>
                    </div>
                </>
            );
        } else {
            const section = getSectionById(currentSectionId);
            if (!section) return null;

            content = (
                <>
                    <H2 className="mb-4 text-2xl font-bold">{section.title}</H2>
                    {section.text && (
                        <div className="space-y-4">
                            <P className="text-gray-700">{section.text}</P>
                            <div className="mt-3 rounded-md bg-gray-50 p-3">
                                <P className="text-sm text-gray-600">
                                    This section outlines important terms and
                                    conditions.
                                </P>
                            </div>
                        </div>
                    )}

                    {section.terms && (
                        <div className="mt-8 space-y-6">
                            {section.terms.map((term, index) => (
                                <div key={index} className="space-y-2">
                                    <H3 className="font-semibold">
                                        {term.number}. {term.term}
                                    </H3>
                                    <P className="mt-1">{term.definition}</P>
                                    <div className="mt-2 rounded-md bg-gray-50 p-3">
                                        <P className="text-sm text-gray-600">
                                            This term defines key concepts used
                                            in the agreement.
                                        </P>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {section.clauses && (
                        <div className="mt-8 space-y-6">
                            {section.clauses.map((clause, index) => (
                                <div key={index} className="space-y-2">
                                    <H3 className="text-lg font-semibold">
                                        {clause.number}.{" "}
                                        {clause.title ? clause.title : ""}
                                    </H3>
                                    <P className="my-2 text-gray-700">
                                        {clause.text}
                                    </P>

                                    {clause.subclauses && (
                                        <div className="ml-6 space-y-4">
                                            {clause.subclauses.map(
                                                (subclause, subIndex) => (
                                                    <div key={subIndex}>
                                                        <P className="font-medium">
                                                            {subclause.letter}.{" "}
                                                            {subclause.text}
                                                        </P>
                                                        <div className="mt-2 rounded-md bg-gray-50 p-2">
                                                            <P className="text-sm text-gray-600">
                                                                This subclause
                                                                provides
                                                                additional
                                                                details and
                                                                requirements.
                                                            </P>
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    )}

                                    <div className="mt-4 rounded-md bg-gray-50 p-3">
                                        <P className="text-sm text-gray-600">
                                            This clause establishes specific
                                            obligations and rights.
                                        </P>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {section.contact_info && (
                        <div className="mt-8 space-y-6">
                            {section.contact_info.covered_entity && (
                                <div className="rounded-md border p-4">
                                    <H3 className="mb-3 font-semibold">
                                        {
                                            section.contact_info.covered_entity
                                                .title
                                        }
                                    </H3>
                                    <div className="space-y-2">
                                        {section.contact_info.covered_entity.fields.map(
                                            (
                                                field: {
                                                    label: string;
                                                    value: string;
                                                },
                                                index: number,
                                            ) => (
                                                <div key={index}>
                                                    {field.label}:
                                                    {field.value && (
                                                        <span className="ml-2">
                                                            {field.value}
                                                        </span>
                                                    )}
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}

                            {section.contact_info.business_associate && (
                                <div className="rounded-md border p-4">
                                    <H3 className="mb-3 font-semibold">
                                        {
                                            section.contact_info
                                                .business_associate.title
                                        }
                                    </H3>
                                    <div className="space-y-2">
                                        {section.contact_info.business_associate.fields.map(
                                            (
                                                field: {
                                                    label: string;
                                                    value: string;
                                                },
                                                index: number,
                                            ) => (
                                                <div key={index}>
                                                    {field.label}:
                                                    {field.value && (
                                                        <span className="ml-2">
                                                            {field.value}
                                                        </span>
                                                    )}
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            );
        }

        return (
            <div className="prose max-w-none">
                <div className="mb-8 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        className="bg-transparent p-2 text-rose-500 hover:bg-orange-100"
                        onClick={() => navigate(-1)}
                    >
                        <ChevronLeft className="mr-2 size-4" />
                        Back
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleDownloadPDF}
                        className="text-brand-primary"
                    >
                        Download PDF
                    </Button>
                </div>

                <div className="flex flex-col gap-8 md:flex-row">
                    <div className="w-full shrink-0 md:w-64">
                        <div className="sticky top-4">
                            <P className="text-sm text-brand-primary">
                                {user?.email}
                            </P>
                            <div className="mt-4">
                                <Button
                                    variant="ghost"
                                    onClick={handleSignOut}
                                    className="hover:text-brand-primary-dark p-2 text-brand-primary"
                                >
                                    <LogOut className="mr-2 size-4" />
                                    Sign out
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="grow">
                        <P className="text-lg text-gray-700">
                            If you are a clinician or practitioner operating in
                            the United States, you must agree to this Business
                            Associate Agreement (BAA) to use {PRODUCT_NAME}.
                        </P>
                        {content}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-rose-300/20 to-orange-500/20">
            <div
                id="layout-container"
                className={clsx(
                    "w-full max-w-md min-h-lg bg-white rounded-md overflow-y-auto flex flex-col items-stretch justify-start shadow-2xl shadow-orange-600/20 border-orange-500/30",
                    !isMobile && "border rounded-none",
                )}
                style={{
                    minHeight: isMobile ? "100vh" : "100vh",
                    maxHeight: isMobile ? "100vh" : "auto",
                    minWidth: isMobile ? "100vw" : "100vh",
                    maxWidth: isMobile ? "100vw" : "100vw",
                    backgroundImage: `url('/background-gradient.png')`,
                    backgroundSize: "100vw 200vw",
                    backgroundPosition: "top -30vh",
                    backgroundRepeat: "no-repeat",
                }}
            >
                <div
                    className={clsx(
                        "w-full h-full",
                        isMobile ? "px-4 py-4" : "px-8 py-8 mx-auto",
                    )}
                    style={{
                        maxWidth: "72rem",
                    }}
                >
                    {/* Header from layout.tsx */}
                    <div className="flex w-full justify-between p-4">
                        <div className="flex items-center gap-4">
                            {!isRoot && (
                                <>
                                    <Button
                                        variant="ghostIcon"
                                        onClick={() => navigate(-1)}
                                        className="bg-transparent p-2 text-rose-500 hover:bg-orange-100"
                                        analyticsId="page back"
                                    >
                                        <ChevronLeft className="size-24" />
                                    </Button>
                                    {!isMobile && <LogoSmall />}
                                </>
                            )}
                        </div>
                        <div className="flex items-center space-x-4">
                            {isAnonymous && (
                                <Link to="/link-account">
                                    <Button
                                        variant="outline"
                                        analyticsId="Link anonymous account"
                                    >
                                        Create an account
                                    </Button>
                                </Link>
                            )}
                            {!isAnonymous && (
                                <div className="flex items-center gap-2">
                                    <div className="text-xs">
                                        <P className="text-sm text-brand-primary">
                                            {user?.email}
                                        </P>
                                    </div>
                                    <Button
                                        analyticsId="Sign out"
                                        onClick={handleSignOut}
                                        variant="ghostIcon"
                                        className="hover:text-brand-primary-dark p-2 text-brand-primary"
                                    >
                                        <LogOut className="size-6" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    {!isMobile && <div className="h-12"></div>}
                    {isMobile && <div className="h-6"></div>}
                    <div className="flex flex-1 flex-col items-center justify-center space-y-8">
                        <DisplayHeader>{baaDocument.title}</DisplayHeader>

                        {/* Added download section with instructions and whitespace */}
                        <div className="mx-auto my-10 w-full max-w-2xl space-y-6 py-8 text-center">
                            <P className="text-lg text-gray-700">
                                If you are a clinician or practitioner operating
                                in the United States and are interested in using
                                {PRODUCT_NAME}, please download this agreement,
                                sign it, and email it back to us at{" "}
                                <A
                                    href={`mailto:${SUPPORT_EMAIL}`}
                                    className="font-bold text-red-500 hover:underline"
                                >
                                    {SUPPORT_EMAIL}
                                </A>
                            </P>

                            <Button
                                onClick={handleDownloadPDF}
                                size="lg"
                                className="px-8"
                            >
                                Download PDF
                            </Button>
                        </div>
                    </div>

                    {/* BAA content */}
                    <div className="mt-8 flex flex-col gap-6 md:flex-row">
                        {/* Sidebar Navigation */}
                        <div className="order-last md:order-first md:w-1/4">
                            <div className="sticky top-24">
                                <nav className="overflow-y-auto rounded-md border-r-2 border-orange-200 bg-white p-8 shadow-md">
                                    <H2 className="mb-4 text-base font-semibold text-zinc-900">
                                        On this page
                                    </H2>
                                    <UL className="space-y-1">
                                        {modifiedSections.map((section) => (
                                            <LI
                                                key={section.id}
                                                className="relative"
                                            >
                                                <button
                                                    onClick={() =>
                                                        setCurrentSection(
                                                            section.id,
                                                        )
                                                    }
                                                    className={`w-full py-1 text-left transition-colors duration-200 ${
                                                        currentSection ===
                                                        section.id
                                                            ? "font-medium text-rose-600"
                                                            : "text-zinc-500 hover:text-rose-600"
                                                    }`}
                                                >
                                                    {section.title}
                                                </button>
                                            </LI>
                                        ))}
                                    </UL>
                                </nav>
                            </div>
                        </div>

                        {/* Content area */}
                        <div
                            className="flex-1 overflow-y-auto rounded-lg bg-white p-8 shadow-md"
                            style={{ maxHeight: "80vh" }}
                        >
                            {renderSectionContent()}
                        </div>
                    </div>

                    {/* Signature section - only show on mobile when explicitly selected */}
                    {typeof window !== "undefined" &&
                        window.innerWidth <= 768 &&
                        currentSection === "signatures" && (
                            <div className="mt-8 rounded-lg border p-6">
                                <H2 className="mb-4 text-xl font-bold">
                                    {baaDocument.signatures.title}
                                </H2>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <P className="font-medium">
                                            {
                                                baaDocument.signatures
                                                    .parties[0]?.name
                                            }
                                        </P>
                                        <P className="text-sm text-gray-600">
                                            {
                                                baaDocument.signatures
                                                    .parties[0]?.role
                                            }
                                        </P>
                                        <div className="mt-4 space-y-4">
                                            {baaDocument.signatures.fields.map(
                                                (field: any, index: number) => (
                                                    <div
                                                        key={index}
                                                        className="space-y-1"
                                                    >
                                                        <P className="text-sm font-medium">
                                                            {field.label}:
                                                        </P>
                                                        <div className="h-8 border-b border-gray-300"></div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <P className="font-medium">&nbsp;</P>
                                        <P className="text-sm text-gray-600">
                                            {
                                                baaDocument.signatures
                                                    .parties[1]?.role
                                            }
                                        </P>
                                        <div className="mt-4 space-y-4">
                                            {baaDocument.signatures.fields.map(
                                                (
                                                    field: {
                                                        label: string;
                                                    },
                                                    index: number,
                                                ) => (
                                                    <div
                                                        key={index}
                                                        className="space-y-1"
                                                    >
                                                        <P className="text-sm font-medium">
                                                            {field.label}:
                                                        </P>
                                                        <div className="h-8 border-b border-gray-300"></div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
}
